import { useAuth } from "@/contexts/AuthContext";
import { signOut } from "@/lib/firebase";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Article, categories, ScheduledArticle, articleStatuses, videoCategories, regions, Video } from "@shared/schema";
import { toArray } from "@/lib/array";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogOut, Loader2, Plus, Trash2, Sparkles, Pencil, Search, Calendar, FileText, BarChart, ClockIcon, XCircle, CheckCircle, ShoppingBag, Building2, Youtube } from "lucide-react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import { LoginForm } from "@/components/LoginForm";
import { AdminMarketplace } from "./AdminMarketplace";
import { AdminBusinessDirectory } from "./AdminBusinessDirectory";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { z } from "zod";

export default function Admin() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  
  // Article management states
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // AI generation states
  const [selectedCategory, setSelectedCategory] = useState<string>("main");
  const [articleCount, setArticleCount] = useState(1);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [generatingNow, setGeneratingNow] = useState(false);
  
  // YouTube video states
  const [showVideoForm, setShowVideoForm] = useState(false);

  const { data: rawArticles, isLoading } = useQuery({
    queryKey: ["/api/articles"],
    enabled: !!user && isAdmin,
  });
  const articles = toArray<Article>(rawArticles);

  const { data: rawScheduledJobs, isLoading: scheduledLoading } = useQuery({
    queryKey: ["/api/scheduled-articles"],
    enabled: !!user && isAdmin,
  });
  const scheduledJobs = toArray<ScheduledArticle>(rawScheduledJobs);

  const { data: rawVideos, isLoading: videosLoading } = useQuery({
    queryKey: ["/api/videos"],
    enabled: !!user && isAdmin,
  });
  const videos = toArray<Video>(rawVideos);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/articles/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      toast({
        title: "삭제 완료",
        description: "기사가 삭제되었습니다.",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/articles/${id}`, { status }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      const statusLabel = articleStatuses.find(s => s.id === variables.status)?.label || variables.status;
      toast({
        title: "상태 변경 완료",
        description: `기사 상태가 '${statusLabel}'(으)로 변경되었습니다.`,
      });
    },
  });

  const generateImmediateMutation = useMutation({
    mutationFn: async ({ category, count }: { category: string; count: number }) => {
      const response = await apiRequest("POST", "/api/generate-original-articles", { category, count });
      return await response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      setGeneratingNow(false);
      toast({
        title: "AI 기사 생성 완료",
        description: `${data.articles?.length || 0}개의 기사가 생성되었습니다.`,
      });
    },
    onError: () => {
      setGeneratingNow(false);
      toast({
        title: "오류",
        description: "기사 생성에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const scheduleGenerationMutation = useMutation({
    mutationFn: ({ category, count, scheduledAt }: { category: string; count: number; scheduledAt: string }) => 
      apiRequest("POST", "/api/scheduled-articles", { category, count, scheduledAt }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-articles"] });
      setScheduleDateTime("");
      toast({
        title: "예약 완료",
        description: "기사 생성이 예약되었습니다.",
      });
    },
    onError: () => {
      toast({
        title: "오류",
        description: "예약에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const cancelScheduledMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/scheduled-articles/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-articles"] });
      toast({
        title: "취소 완료",
        description: "예약이 취소되었습니다.",
      });
    },
  });

  const scrapeAndGenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/scrape-and-generate", {});
      return await response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      toast({
        title: "크롤링 및 생성 완료",
        description: `${data.scraped || 0}개 수집, ${data.generated || 0}개 기사 생성됨`,
      });
    },
    onError: () => {
      toast({
        title: "오류",
        description: "크롤링 및 생성에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  // YouTube video mutations
  const createVideoMutation = useMutation({
    mutationFn: (data: z.infer<typeof videoFormSchema>) => 
      apiRequest("POST", "/api/videos", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      setShowVideoForm(false);
      videoForm.reset();
      toast({
        title: "등록 완료",
        description: "비디오가 성공적으로 등록되었습니다.",
      });
    },
    onError: () => {
      toast({
        title: "오류",
        description: "비디오 등록에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const deleteVideoMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/videos/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      toast({
        title: "삭제 완료",
        description: "비디오가 삭제되었습니다.",
      });
    },
  });

  // YouTube URL에서 비디오 ID 추출하는 헬퍼 함수
  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Video form schema
  const videoFormSchema = z.object({
    title: z.string().min(1, "제목을 입력하세요"),
    titleKo: z.string().optional(),
    description: z.string().optional(),
    descriptionKo: z.string().optional(),
    youtubeUrl: z.string().min(1, "YouTube URL을 입력하세요").refine(
      (url) => extractYouTubeId(url) !== null,
      "올바른 YouTube URL을 입력하세요"
    ),
    youtubeId: z.string().optional(),
    category: z.string().min(1, "카테고리를 선택하세요"),
    region: z.string().default("ALL"),
    status: z.string().default("published"),
    isFeatured: z.boolean().default(false),
    isShorts: z.boolean().default(false),
    thumbnailUrl: z.string().optional(),
  });

  const videoForm = useForm<z.infer<typeof videoFormSchema>>({
    resolver: zodResolver(videoFormSchema),
    defaultValues: {
      title: "",
      titleKo: "",
      description: "",
      descriptionKo: "",
      youtubeUrl: "",
      youtubeId: "",
      category: "stock-usa",
      region: "ALL",
      status: "published",
      isFeatured: false,
      isShorts: false,
      thumbnailUrl: "",
    },
  });

  const onVideoSubmit = (data: z.infer<typeof videoFormSchema>) => {
    const youtubeId = extractYouTubeId(data.youtubeUrl);
    if (!youtubeId) {
      toast({
        title: "오류",
        description: "YouTube URL에서 비디오 ID를 추출할 수 없습니다.",
        variant: "destructive",
      });
      return;
    }

    createVideoMutation.mutate({
      ...data,
      youtubeId,
      thumbnailUrl: data.thumbnailUrl || `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`,
    });
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>접근 권한 없음</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              이 페이지는 관리자만 접근할 수 있습니다.
            </p>
            <div className="flex gap-2">
              <Link href="/">
                <Button data-testid="button-go-home">홈으로 이동</Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => signOut()}
                data-testid="button-logout"
              >
                로그아웃
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter and paginate articles
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (article.titleKo || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || article.category === categoryFilter;
    const matchesStatus = statusFilter === "all" || article.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleGenerateNow = () => {
    if (articleCount < 1 || articleCount > 10) {
      toast({
        title: "오류",
        description: "1-10개 사이의 기사를 생성할 수 있습니다.",
        variant: "destructive",
      });
      return;
    }
    setGeneratingNow(true);
    generateImmediateMutation.mutate({ category: selectedCategory, count: articleCount });
  };

  const handleScheduleGeneration = () => {
    if (!scheduleDateTime) {
      toast({
        title: "오류",
        description: "예약 시간을 선택하세요.",
        variant: "destructive",
      });
      return;
    }
    if (articleCount < 1 || articleCount > 10) {
      toast({
        title: "오류",
        description: "1-10개 사이의 기사를 생성할 수 있습니다.",
        variant: "destructive",
      });
      return;
    }
    scheduleGenerationMutation.mutate({ 
      category: selectedCategory, 
      count: articleCount, 
      scheduledAt: scheduleDateTime 
    });
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-500",
      processing: "bg-blue-500",
      completed: "bg-green-500",
      failed: "bg-red-500",
      cancelled: "bg-gray-500",
    };
    const labels: Record<string, string> = {
      pending: "대기중",
      processing: "처리중",
      completed: "완료",
      failed: "실패",
      cancelled: "취소됨",
    };
    return <Badge className={colors[status]}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">관리자 대시보드</h1>
            <p className="text-muted-foreground">
              기사 관리, AI 생성, 예약 관리를 한 곳에서
            </p>
          </div>
          <div className="flex gap-2">
            <Link href="/">
              <Button variant="outline" data-testid="button-home">
                홈으로
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => signOut()}
              data-testid="button-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              로그아웃
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">전체 기사</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{articles?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">예약된 작업</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {scheduledJobs.filter(j => j.status === "pending").length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">오늘 발행</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {articles.filter(a => {
                  const today = new Date().toDateString();
                  return new Date(a.publishedAt).toDateString() === today;
                }).length || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">카테고리 수</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categories.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="articles" className="space-y-4">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="articles" data-testid="tab-articles">
              <FileText className="h-4 w-4 mr-2" />
              기사 관리
            </TabsTrigger>
            <TabsTrigger value="generate" data-testid="tab-generate">
              <Sparkles className="h-4 w-4 mr-2" />
              AI 생성
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled">
              <Calendar className="h-4 w-4 mr-2" />
              예약 관리
            </TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads">
              <BarChart className="h-4 w-4 mr-2" />
              광고 관리
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">
              <Youtube className="h-4 w-4 mr-2" />
              유튜브
            </TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="tab-marketplace">
              <ShoppingBag className="h-4 w-4 mr-2" />
              핫샵
            </TabsTrigger>
            <TabsTrigger value="business-directory" data-testid="tab-business-directory">
              <Building2 className="h-4 w-4 mr-2" />
              교민 업체주소록
            </TabsTrigger>
          </TabsList>

          {/* Articles Management Tab */}
          <TabsContent value="articles" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>전체 기사 관리</CardTitle>
                    <CardDescription>기사 검색, 수정, 삭제</CardDescription>
                  </div>
                  <Link href="/admin/new">
                    <Button data-testid="button-new-article">
                      <Plus className="h-4 w-4 mr-2" />
                      새 기사 작성
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search and Filter */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="기사 제목 검색..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-10"
                        data-testid="input-search-articles"
                      />
                    </div>
                  </div>
                  <Select
                    value={categoryFilter}
                    onValueChange={(value) => {
                      setCategoryFilter(value);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-40" data-testid="select-category-filter">
                      <SelectValue placeholder="카테고리" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={statusFilter}
                    onValueChange={(value) => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-40" data-testid="select-status-filter">
                      <SelectValue placeholder="상태" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {articleStatuses.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Articles Table */}
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>제목</TableHead>
                            <TableHead className="w-24">작성자</TableHead>
                            <TableHead className="w-28">카테고리</TableHead>
                            <TableHead className="w-24">상태</TableHead>
                            <TableHead className="w-36">발행일</TableHead>
                            <TableHead className="w-48 text-right">작업</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedArticles.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-muted-foreground">
                                기사가 없습니다
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedArticles.map((article) => {
                              const status = articleStatuses.find(s => s.id === (article.status || 'published'));
                              const statusColor = status?.color || 'gray';
                              
                              return (
                              <TableRow key={article.id}>
                                <TableCell className="font-medium">
                                  <div className="flex flex-col">
                                    <span>{article.titleKo || article.title}</span>
                                    {article.isBreaking && (
                                      <Badge variant="destructive" className="w-fit mt-1">
                                        속보
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm text-muted-foreground">
                                    {article.author ? `${article.author} 기자` : "OZK News"}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {categories.find(c => c.id === article.category)?.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant="outline"
                                    className={
                                      statusColor === 'green' ? 'bg-green-100 text-green-800 border-green-300' :
                                      statusColor === 'blue' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                      statusColor === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                      'bg-gray-100 text-gray-800 border-gray-300'
                                    }
                                  >
                                    {status?.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm">
                                    {format(new Date(article.publishedAt), "PPp", { locale: ko })}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex gap-2 justify-end">
                                    {article.status === 'pending' && (
                                      <>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => updateStatusMutation.mutate({ id: article.id, status: 'published' })}
                                          data-testid={`button-approve-${article.id}`}
                                          className="hover-elevate"
                                        >
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          승인
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => updateStatusMutation.mutate({ id: article.id, status: 'draft' })}
                                          data-testid={`button-reject-${article.id}`}
                                          className="hover-elevate"
                                        >
                                          <XCircle className="h-4 w-4 mr-1" />
                                          거부
                                        </Button>
                                      </>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setLocation(`/admin/edit/${article.id}`)}
                                      data-testid={`button-edit-${article.id}`}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        if (confirm("정말 삭제하시겠습니까?")) {
                                          deleteMutation.mutate(article.id);
                                        }
                                      }}
                                      data-testid={`button-delete-${article.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4">
                        <div className="text-sm text-muted-foreground">
                          총 {filteredArticles.length}개 중 {(currentPage - 1) * itemsPerPage + 1}-
                          {Math.min(currentPage * itemsPerPage, filteredArticles.length)}개 표시
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            data-testid="button-prev-page"
                          >
                            이전
                          </Button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                data-testid={`button-page-${pageNum}`}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            data-testid="button-next-page"
                          >
                            다음
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Generation Tab */}
          <TabsContent value="generate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI 기사 생성</CardTitle>
                <CardDescription>
                  카테고리별로 AI가 자동으로 기사를 생성합니다
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>카테고리</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger data-testid="select-gen-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>생성 개수 (1-10개)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={articleCount}
                      onChange={(e) => setArticleCount(parseInt(e.target.value) || 1)}
                      data-testid="input-article-count"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    onClick={handleGenerateNow}
                    disabled={generatingNow}
                    className="flex-1"
                    data-testid="button-generate-now"
                  >
                    {generatingNow ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        생성 중...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        즉시 생성
                      </>
                    )}
                  </Button>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4">예약 생성</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>예약 시간</Label>
                      <Input
                        type="datetime-local"
                        value={scheduleDateTime}
                        onChange={(e) => setScheduleDateTime(e.target.value)}
                        data-testid="input-schedule-datetime"
                      />
                    </div>
                    <Button
                      onClick={handleScheduleGeneration}
                      disabled={!scheduleDateTime}
                      variant="outline"
                      className="w-full"
                      data-testid="button-schedule-generation"
                    >
                      <ClockIcon className="h-4 w-4 mr-2" />
                      예약하기
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4">외부 소스 크롤링</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Korean Herald, Top Digital, KCM Weekly, Naver News, ABC AU, SBS Korean에서 최신 기사를 수집하고 AI로 재작성합니다
                  </p>
                  <Button
                    onClick={() => scrapeAndGenerateMutation.mutate()}
                    disabled={scrapeAndGenerateMutation.isPending}
                    variant="secondary"
                    className="w-full"
                    data-testid="button-scrape-generate"
                  >
                    {scrapeAndGenerateMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        크롤링 중...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        외부 소스 크롤링 및 생성
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduled Jobs Tab */}
          <TabsContent value="scheduled" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>예약된 작업</CardTitle>
                <CardDescription>
                  예약된 기사 생성 목록 및 관리
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scheduledLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : scheduledJobs && scheduledJobs.length > 0 ? (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>카테고리</TableHead>
                          <TableHead>생성 개수</TableHead>
                          <TableHead>예약 시간</TableHead>
                          <TableHead>상태</TableHead>
                          <TableHead className="text-right">작업</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scheduledJobs.map((job) => (
                          <TableRow key={job.id}>
                            <TableCell>
                              <Badge variant="outline">
                                {categories.find(c => c.id === job.category)?.label}
                              </Badge>
                            </TableCell>
                            <TableCell>{job.count}개</TableCell>
                            <TableCell>
                              {format(new Date(job.scheduledAt), "PPp", { locale: ko })}
                            </TableCell>
                            <TableCell>{getStatusBadge(job.status)}</TableCell>
                            <TableCell className="text-right">
                              {job.status === "pending" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (confirm("예약을 취소하시겠습니까?")) {
                                      cancelScheduledMutation.mutate(job.id);
                                    }
                                  }}
                                  data-testid={`button-cancel-${job.id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    예약된 작업이 없습니다
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ads Management Tab */}
          <TabsContent value="ads" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>광고 관리</CardTitle>
                <CardDescription>
                  광고 페이지로 이동하여 관리하세요
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link href="/admin/ads">
                  <Button className="w-full" data-testid="button-go-ads">
                    <BarChart className="h-4 w-4 mr-2" />
                    광고 관리 페이지로 이동
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketplace Management Tab */}
          <TabsContent value="marketplace" className="space-y-4">
            <AdminMarketplace />
          </TabsContent>

          {/* Business Directory Management Tab */}
          <TabsContent value="business-directory" className="space-y-4">
            <AdminBusinessDirectory />
          </TabsContent>

          {/* YouTube Videos Management Tab */}
          <TabsContent value="videos" className="space-y-4">
            {/* Add Video Form */}
            {showVideoForm ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Youtube className="h-5 w-5" />
                      YouTube 비디오 등록
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowVideoForm(false)}
                      data-testid="button-cancel-video"
                    >
                      취소
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Form {...videoForm}>
                    <form onSubmit={videoForm.handleSubmit(onVideoSubmit)} className="space-y-4">
                      <FormField
                        control={videoForm.control}
                        name="youtubeUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>YouTube URL *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="https://www.youtube.com/watch?v=... 또는 https://youtube.com/shorts/..."
                                {...field}
                                data-testid="input-youtube-url"
                              />
                            </FormControl>
                            <FormDescription>
                              일반 비디오 또는 숏츠 URL을 입력하세요
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={videoForm.control}
                          name="title"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>제목 (영문) *</FormLabel>
                              <FormControl>
                                <Input placeholder="US Stock Market Analysis" {...field} data-testid="input-title" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={videoForm.control}
                          name="titleKo"
                          render={({ field }) => {
                            const { value, ...rest } = field;
                            return (
                              <FormItem>
                                <FormLabel>제목 (한글)</FormLabel>
                                <FormControl>
                                  <Input placeholder="미국 주식 시황 분석" {...rest} value={value ?? ""} data-testid="input-title-ko" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={videoForm.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>카테고리 *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-category">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {videoCategories.map((cat) => (
                                    <SelectItem key={cat.id} value={cat.id}>
                                      {cat.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={videoForm.control}
                          name="region"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>지역</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value ?? "ALL"}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-region">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {regions.map((region) => (
                                    <SelectItem key={region.id} value={region.id}>
                                      {region.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={videoForm.control}
                        name="description"
                        render={({ field }) => {
                          const { value, ...rest } = field;
                          return (
                            <FormItem>
                              <FormLabel>설명 (영문)</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Detailed analysis of US stock market..."
                                  {...rest}
                                  value={value ?? ""}
                                  data-testid="textarea-description"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={videoForm.control}
                        name="descriptionKo"
                        render={({ field }) => {
                          const { value, ...rest } = field;
                          return (
                            <FormItem>
                              <FormLabel>설명 (한글)</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="미국 주식 시장에 대한 상세 분석..."
                                  {...rest}
                                  value={value ?? ""}
                                  data-testid="textarea-description-ko"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <div className="flex gap-4">
                        <FormField
                          control={videoForm.control}
                          name="isShorts"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value ?? false}
                                  onCheckedChange={field.onChange}
                                  data-testid="checkbox-is-shorts"
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer">
                                YouTube Shorts
                              </FormLabel>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={videoForm.control}
                          name="isFeatured"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value ?? false}
                                  onCheckedChange={field.onChange}
                                  data-testid="checkbox-is-featured"
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer">
                                추천 영상
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={createVideoMutation.isPending}
                        data-testid="button-submit-video"
                      >
                        {createVideoMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            등록 중...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2 h-4 w-4" />
                            비디오 등록
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Youtube className="h-5 w-5" />
                      YouTube 비디오 관리
                    </span>
                    <Button onClick={() => setShowVideoForm(true)} data-testid="button-add-video">
                      <Plus className="mr-2 h-4 w-4" />
                      비디오 등록
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    총 {videos?.length || 0}개의 비디오
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {videosLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : !videos || videos.length === 0 ? (
                    <div className="text-center py-12">
                      <Youtube className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">등록된 비디오가 없습니다</h3>
                      <p className="text-muted-foreground mb-6">
                        첫 번째 YouTube 비디오를 등록해보세요
                      </p>
                      <Button onClick={() => setShowVideoForm(true)} data-testid="button-add-first-video">
                        <Plus className="mr-2 h-4 w-4" />
                        비디오 등록하기
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {videos.map((video) => (
                        <Card key={video.id}>
                          <CardContent className="p-4">
                            <div className="flex gap-4">
                              <img
                                src={video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`}
                                alt={video.titleKo || video.title}
                                className="w-32 h-20 object-cover rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h4 className="font-semibold">{video.titleKo || video.title}</h4>
                                    {video.titleKo && (
                                      <p className="text-sm text-muted-foreground">{video.title}</p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteVideoMutation.mutate(video.id)}
                                    disabled={deleteVideoMutation.isPending}
                                    data-testid={`button-delete-video-${video.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  <Badge variant="secondary">
                                    {videoCategories.find(c => c.id === video.category)?.label}
                                  </Badge>
                                  {video.region && video.region !== "ALL" && (
                                    <Badge variant="outline">
                                      {regions.find(r => r.id === video.region)?.label}
                                    </Badge>
                                  )}
                                  {video.isShorts && (
                                    <Badge variant="outline">Shorts</Badge>
                                  )}
                                  {video.isFeatured && (
                                    <Badge>추천</Badge>
                                  )}
                                  <Badge variant="outline">조회수: {video.views}</Badge>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
